/**
 * `ExerciseTemplateResponse.executions` → `ExerciseDraft.executions`.
 *
 * The read-only half of the exercise: what the client was GIVEN at this slot and what they did with
 * it, ascending by cycle. It rides on `GET /plan-template/{id}/full` so the builder performs no join
 * and no second fetch — the slot token, the template sets, the live edit boundary and the lived
 * occurrences are all read at one moment (ADR `2026-09-15-executions-ride-on-the-full-read`).
 *
 * ── Three rules, and each of them is the point of the module ─────────────────────────────────
 *
 * 1. **`null` and `[]` are different answers and are never collapsed.** `null` means the question
 *    does not apply — a library entry, an unassigned template, a fork nobody is training — and the
 *    sheet then draws the TEMPLATE variant with no now-marker and no verdicts. `[]` means a live
 *    fork whose client has trained nothing here yet, which is the live variant with an empty past.
 *    Reading an absent field as `[]` would put „TU JEST KLIENT" over a library template.
 * 2. **Nothing here judges.** `delivered` and `shortfallFields` are carried verbatim from the
 *    backend's own `PreviousCountedSet`, the same function the start-time resolver calls (R12). This
 *    module has no comparison in it and must never grow one: the moment it compares an actual
 *    against a prescription, the sheet and the next prescription can disagree.
 * 3. **The prescription is the FROZEN one.** `prescribed*` is what was stamped onto the row when the
 *    client started the workout (R2), not the template's current cell — which is exactly why the
 *    sheet can show „zadane 100 × 8 / zrobione 100 × 6" after the trainer has since edited the plan.
 *
 * A row whose slot token was blank never reaches here: the backend skips those (rows predating the
 * token columns, and ad-hoc work), so an entry always belongs to the slot it is stamped on.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type {
  TrainWithMeModulesPlansDtosResponsesSlotExecutionResponse,
  TrainWithMeModulesPlansDtosResponsesSlotExecutionSetResponse,
} from '~/shared/api/generated/model';

import { parseMeasurementField, type MeasurementFieldKey } from '~/shared/measurement';

import type { SlotExecution, SlotExecutionSet } from './planDraft.types';

type SlotExecutionResponse = TrainWithMeModulesPlansDtosResponsesSlotExecutionResponse;
type SlotExecutionSetResponse = TrainWithMeModulesPlansDtosResponsesSlotExecutionSetResponse;

/**
 * The wire's column pairs, per measurement field: the frozen prescription and the logged actual.
 *
 * It is a TABLE rather than a `prescribed${Pascal}` derivation because the wire's names are not
 * derivable — `duration` is `prescribedDurationSeconds` / `durationSeconds` and `distance` is
 * `prescribedDistanceMeters` / `distanceMeters`. A derivation would silently drop exactly those two.
 *
 * `tempo` is deliberately absent: it is a text notation on both sides, so it carries no number for
 * a cell to print or a chart to plot. It can still appear in `shortfallFields`, which is a list of
 * COLUMN NAMES rather than of values, and it is read there like any other.
 */
const COLUMNS: Readonly<
  Partial<
    Record<
      MeasurementFieldKey,
      {
        readonly prescribed: keyof SlotExecutionSetResponse;
        readonly actual: keyof SlotExecutionSetResponse;
      }
    >
  >
> = {
  weight: { prescribed: 'prescribedWeight', actual: 'weight' },
  reps: { prescribed: 'prescribedReps', actual: 'reps' },
  duration: { prescribed: 'prescribedDurationSeconds', actual: 'durationSeconds' },
  distance: { prescribed: 'prescribedDistanceMeters', actual: 'distanceMeters' },
  rounds: { prescribed: 'prescribedRounds', actual: 'rounds' },
  speed: { prescribed: 'prescribedSpeed', actual: 'speed' },
  rpe: { prescribed: 'prescribedRpe', actual: 'rpe' },
  rir: { prescribed: 'prescribedRir', actual: 'rir' },
};

/** One numeric cell of an execution row, or `undefined` where the row carries none. */
function numberAt(
  row: SlotExecutionSetResponse,
  key: keyof SlotExecutionSetResponse,
): number | undefined {
  const raw = row[key];
  return typeof raw === 'number' ? raw : undefined;
}

/**
 * Both sides of one row, as two sparse field maps.
 *
 * SPARSE is load-bearing: "the row was not priced on this column" and "the row was priced at zero"
 * are different facts, and zero is a real prescription — a bodyweight movement, RIR 0 to failure.
 */
function valuesOf(row: SlotExecutionSetResponse): {
  readonly prescribed: Partial<Record<MeasurementFieldKey, number>>;
  readonly actual: Partial<Record<MeasurementFieldKey, number>>;
} {
  const prescribed: Partial<Record<MeasurementFieldKey, number>> = {};
  const actual: Partial<Record<MeasurementFieldKey, number>> = {};
  for (const [field, pair] of Object.entries(COLUMNS) as [
    MeasurementFieldKey,
    { prescribed: keyof SlotExecutionSetResponse; actual: keyof SlotExecutionSetResponse },
  ][]) {
    const asked = numberAt(row, pair.prescribed);
    if (asked !== undefined) prescribed[field] = asked;
    const did = numberAt(row, pair.actual);
    if (did !== undefined) actual[field] = did;
  }
  return { prescribed, actual };
}

function setFromWire(row: SlotExecutionSetResponse): SlotExecutionSet {
  const { prescribed, actual } = valuesOf(row);
  return {
    setIndex: row.setIndex ?? 0,
    // Verbatim. A set type a newer server adds must still line the row up with `templateSets`, and
    // `countsAsWork` already answers "does this count" from the one discriminator there is.
    setType: row.setType ?? '',
    isCompleted: row.isCompleted ?? false,
    prescribed,
    actual,
    prescribedCeiling: row.prescribedCeiling ?? null,
    prescribedPercent: row.prescribedPercent ?? null,
    // A column name this build cannot parse is DROPPED rather than carried as a string: every
    // reader of this list asks "is the viewed column short", and an unparseable entry can answer
    // that for no column. The verdict itself is unaffected — it rides on `delivered`.
    shortfallFields: (row.shortfallFields ?? [])
      .map((name) => parseMeasurementField(name))
      .filter((field): field is MeasurementFieldKey => field !== null),
  };
}

/**
 * The lived record for one exercise slot, or `null` where the wire did not answer.
 *
 * Sorted by cycle here rather than trusted: every consumer indexes by cycle and the chart plots the
 * list in order, so one out-of-order entry would draw a line that goes backwards.
 */
export function executionsFromWire(
  wire: readonly SlotExecutionResponse[] | null | undefined,
): readonly SlotExecution[] | null {
  if (wire == null) return null;
  return wire
    .map<SlotExecution>((execution) => ({
      cycleIndex: execution.cycleIndex ?? 0,
      workoutExecutionId: execution.workoutExecutionId ?? 0,
      status: execution.status ?? '',
      // NOT `?? false`. An absent flag is „the wire did not say", and `false` is „the backend
      // refused the step" — collapsing the two invents a verdict here, in the module whose second
      // rule is that nothing here judges. `executionsToMatrixInputs` reads the null and draws no
      // verdict at all.
      delivered: execution.delivered ?? null,
      sets: (execution.sets ?? [])
        .map(setFromWire)
        .sort((a, b) => a.setIndex - b.setIndex),
    }))
    .sort((a, b) => a.cycleIndex - b.cycleIndex);
}
